// Test-taker sign-in: the sign-in page, "My profiles", and the "Save your results" card on a
// results page. A password is set from a take this device took (the API checks the take's
// token), so typing someone else's email never opens their results.
//
// /sign-in is the one sign-in page for everyone (Edward, 20 Oct: he typed his staff password
// there and was told it didn't match). Staff credentials are tried first, through the same
// call /admin makes, and land in the admin (its two-step code included); anyone else is signed
// in to their profiles as before. Staff who are already signed in get a "Go to admin" link here
// and on /me, from a real check of the staff session.

import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { ConnectedAppsList } from "../components/ConnectedApps";
import { Loading, Page } from "../components/Page";
import { ApiError, api, type MyTeam, type SignInStep, type TakeAccount } from "../lib/api";
import { PasskeyError, addPasskey, passkeysSupported, prepareAdd, prepareSignIn, signInWithPasskey } from "../lib/personPasskeys";
import { MySignIn } from "./MySignIn";
import { staffSignInWithPassword, useStaffSignedIn } from "../lib/staff";
import { useEmailEnabled } from "../lib/useEmailEnabled";
import type { MyConnections, MyDashboard, MyProfile } from "../lib/types";

export const MIN_PASSWORD = 8;

const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const TOO_MANY_TRIES = "Too many tries. Wait a few minutes and try again.";
const TRY_AGAIN = "We couldn't sign you in. Try again.";

/** How the staff check ended when it didn't sign the person in. */
type StaffMiss = "refused" | "too_many" | "broken";

/**
 * The one message when a sign-in is refused. The API's 401 says the same thing for an unknown
 * email, a wrong password and a staff email, so it can be shown as is. Two cases are worth
 * saying over "don't match", and neither says anything about the email: the staff check was
 * rate-limited (per IP), or it broke (a 5xx), in which case the password may well be right.
 */
function signInError(err: unknown, staff: StaffMiss | null): string {
  if (err instanceof ApiError && err.status === 429) return err.message;
  if (err instanceof ApiError && err.status === 401) {
    if (staff === "too_many") return TOO_MANY_TRIES;
    if (staff === "broken") return TRY_AGAIN;
    return err.message;
  }
  return "We couldn't sign you in. Check your connection and try again.";
}

/**
 * The email-and-password form, shared by the sign-in page and the "Connect an app" page so
 * both sign in the same way (same call, same remembered session, same messages).
 *
 * With `onStaffSignedIn`, the credentials are tried as a staff sign-in first (the same call
 * /admin makes; see lib/staff.ts) and, when they're right, that's where the person goes.
 * Otherwise they're tried as a test-taker exactly as before. A staff refusal is a 401 whether
 * the email is staff or not, and the taker check answers the same for an unknown email and a
 * wrong password, so one message covers everyone.
 *
 * Limits (app.ts): only refused tries count, per email from one IP (10 per 10 minutes) and,
 * more loosely, per IP. A taker's try is refused as staff first, which counts only against
 * their own email, so takers on one shared IP (a venue's wifi) don't lock staff out. In
 * production every try is also one request on Better Auth's own limiter for /sign-in* (3 per
 * 10 seconds per IP). Per account: the taker account
 * pauses after five wrong passwords, so a staff member who mistypes five times pauses a taker
 * account on the same email for 15 minutes, and no more than that: staff passwords have no
 * per-account lock to trip.
 */
export function PersonSignInForm({
  onSignedIn,
  onStaffSignedIn,
  initialMode = "link",
  className = "",
}: {
  onSignedIn: () => void;
  onStaffSignedIn?: () => void;
  /**
   * "link": an emailed sign-in link first, the main way back in (Edward, Oct 23: no password to
   * remember). "password" where the person must come straight back to this page (Connect).
   */
  initialMode?: "password" | "link";
  className?: string;
}) {
  const emailId = useId();
  const passwordId = useId();
  const [chosenMode, setMode] = useState<"password" | "link">(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const emailSetting = useEmailEnabled();
  const emailEnabled = emailSetting === true;
  // While email is off there are no links to send: the password form, as before.
  const emailOff = emailSetting === false;
  const mode = chosenMode === "link" && emailOff ? "password" : chosenMode;
  const [passkeys] = useState(passkeysSupported);

  // Ready a passkey challenge before the tap (Safari needs the sheet to open from the tap itself).
  useEffect(() => {
    if (!passkeys) return;
    const ready = () => void prepareSignIn().catch(() => undefined);
    ready();
    const timer = window.setInterval(ready, 3 * 60_000);
    return () => window.clearInterval(timer);
  }, [passkeys]);

  if (challenge) return <CodeStep challenge={challenge} onSignedIn={onSignedIn} onStartOver={() => setChallenge(null)} className={className} />;

  const finish = (step: SignInStep) => (step.done ? onSignedIn() : setChallenge(step.challenge));

  const validEmail = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validEmail()) return setError("Enter your email address.");
    if (mode === "link") {
      setBusy(true);
      try {
        setLinkSent((await api.requestSignInLink(email.trim())).message);
      } catch (err) {
        setError(err instanceof ApiError && err.status === 429 ? TOO_MANY_TRIES : "We couldn't send that. Check your connection and try again.");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!password) return setError("Enter your password.");
    setBusy(true);
    let staff: StaffMiss | null = null;
    try {
      if (onStaffSignedIn) {
        try {
          await staffSignInWithPassword(email.trim(), password);
          onStaffSignedIn();
          return;
        } catch (err) {
          // Refused (401, or 400 for an email Better Auth won't read), limited (429) or broken
          // (anything else): try them as a test-taker. A network failure (status 0) has nothing
          // to fall back to.
          if (!(err instanceof ApiError) || err.status === 0) throw err;
          staff = err.status === 401 || err.status === 400 ? "refused" : err.status === 429 ? "too_many" : "broken";
        }
      }
      finish(await api.signIn(email.trim(), password));
    } catch (err) {
      setBusy(false);
      setError(signInError(err, staff));
    }
  };

  const usePasskey = async () => {
    setError(null);
    setBusy(true);
    try {
      finish(await signInWithPasskey());
    } catch (err) {
      setBusy(false);
      setError(err instanceof PasskeyError || (err instanceof ApiError && err.status === 401) ? err.message : TRY_AGAIN);
      void prepareSignIn().catch(() => undefined);
    }
  };

  const switchTo = (next: "password" | "link") => {
    setMode(next);
    setError(null);
    setLinkSent(null);
  };

  return (
    <div className={className}>
      <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
        <label htmlFor={emailId} className="text-[15px] font-semibold">
          Email
        </label>
        <input
          id={emailId}
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="off"
          spellCheck={false}
          className="field-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {mode === "password" ? (
          <>
            <label htmlFor={passwordId} className="mt-2 text-[15px] font-semibold">
              Password
            </label>
            <input
              id={passwordId}
              type="password"
              name="password"
              autoComplete="current-password"
              className="field-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
        ) : null}
        {linkSent ? (
          <div role="status" className="text-pretty rounded-[12px] bg-soft px-4 py-3 text-[15px] leading-snug">
            <p>{linkSent}</p>
            <p className="mt-2 text-muted">
              Not there? Check your spam folder, or{" "}
              <button type="button" className="text-accent-text underline" onClick={() => setLinkSent(null)}>
                send another
              </button>
              .
            </p>
          </div>
        ) : (
          <button type="submit" className="btn btn-primary mt-2 w-full" disabled={busy}>
            {mode === "link" ? (busy ? "Sending…" : "Email me a sign-in link") : busy ? "Signing in…" : "Sign in"}
          </button>
        )}
        {error ? (
          <p role="alert" className="text-[15px] text-danger">
            {error}
          </p>
        ) : null}
      </form>
      <div className="mt-4 flex flex-col items-start gap-3 text-[15px]">
        {passkeys ? (
          <button type="button" className="btn btn-secondary w-full" disabled={busy} onClick={() => void usePasskey()}>
            Sign in with a passkey
          </button>
        ) : null}
        {mode === "password" && emailEnabled ? (
          <button type="button" className="text-accent-text underline" onClick={() => switchTo("link")}>
            Email me a sign-in link instead
          </button>
        ) : null}
        {mode === "link" ? (
          <button type="button" className="text-accent-text underline" onClick={() => switchTo("password")}>
            Sign in with a password instead
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The code step: the password or link was right and the authenticator app is on. A 6-digit
 * code from the app, or a backup code. Used by the sign-in form and the emailed-link page.
 */
export function CodeStep({
  challenge,
  onSignedIn,
  onStartOver,
  className = "",
}: {
  challenge: string;
  onSignedIn: () => void;
  onStartOver: () => void;
  className?: string;
}) {
  const id = useId();
  const [useBackup, setUseBackup] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const v = value.trim();
    if (useBackup ? !v : !/^\d{6}$/.test(v)) return setError(useBackup ? "Enter one of your backup codes." : "Enter the 6-digit code from your app.");
    setBusy(true);
    try {
      await api.finishWithCode(challenge, useBackup ? { backupCode: v } : { code: v });
      onSignedIn();
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.status === 404) setExpired(true);
      setError(err instanceof ApiError && err.status !== 500 ? err.message : TRY_AGAIN);
    }
  };

  return (
    <form className={`flex flex-col gap-3 ${className}`} onSubmit={submit} noValidate data-testid="code-step">
      <p className="text-pretty text-[16px] leading-snug">
        {useBackup ? "Enter one of the backup codes you saved." : "Open your authenticator app and enter the code for Gifts Profile."}
      </p>
      <label htmlFor={id} className="text-[15px] font-semibold">
        {useBackup ? "Backup code" : "6-digit code"}
      </label>
      <input
        id={id}
        className={useBackup ? "field-input font-mono" : "field-input text-center text-[22px] tracking-[0.3em] tabular-nums"}
        inputMode={useBackup ? "text" : "numeric"}
        autoComplete={useBackup ? "off" : "one-time-code"}
        autoCapitalize="off"
        spellCheck={false}
        maxLength={useBackup ? 20 : 6}
        value={value}
        onChange={(e) => setValue(useBackup ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
      />
      {expired ? (
        <button type="button" className="btn btn-primary mt-2 w-full" onClick={onStartOver}>
          Sign in again
        </button>
      ) : (
        <button type="submit" className="btn btn-primary mt-2 w-full" disabled={busy}>
          {busy ? "Checking…" : "Continue"}
        </button>
      )}
      {error ? (
        <p role="alert" className="text-[15px] text-danger">
          {error}
        </p>
      ) : null}
      <button
        type="button"
        className="self-start text-[15px] text-accent-text underline"
        onClick={() => {
          setUseBackup((b) => !b);
          setValue("");
          setError(null);
        }}
      >
        {useBackup ? "Use your authenticator app" : "Use a backup code"}
      </button>
    </form>
  );
}

/** A way into the admin for staff who are signed in there. Shown only after a real session check. */
function GoToAdminLink({ className = "" }: { className?: string }) {
  return (
    <Link to="/admin" className={className} data-testid="go-to-admin">
      Go to admin
    </Link>
  );
}

export function SignInPage() {
  const navigate = useNavigate();
  // Already signed in as staff (say, sent here by /me because they have no taker session):
  // trying their credentials as staff again would only send them round to the admin, so the
  // form signs them in to their profiles, and the admin is one tap away. So is signing out of
  // it: on a shared device the next person shouldn't inherit a staff session.
  const { staff, signOut: staffSignOut } = useStaffSignedIn();
  // "Forgot it?" only while a reset link can actually be emailed.
  const emailEnabled = useEmailEnabled() === true;
  return (
    <Page>
      <h1 className="heading mt-10 text-[30px] leading-[1.1]">Sign in to see your profiles</h1>
      {emailEnabled ? (
        <p className="text-pretty mt-3 text-[16px] leading-snug text-muted">
          Enter the email you took the profile with and we'll email you a sign-in link. No password needed, and it works for profiles you
          haven't finished too.
        </p>
      ) : null}
      {staff ? (
        <section className="mt-6 rounded-[16px] border border-line bg-panel p-4" aria-label="Signed in to the admin" data-testid="staff-signed-in">
          <p className="text-[15px] leading-snug">
            You're signed in to the admin as <strong className="break-all font-semibold">{staff.email}</strong>.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <GoToAdminLink className="btn btn-primary inline-flex min-h-11 px-4 text-[14px] no-underline" />
            <button type="button" className="btn btn-secondary min-h-11 px-4 text-[14px]" onClick={() => void staffSignOut()}>
              Sign out of admin
            </button>
          </div>
          <p className="mt-3 text-[14px] leading-snug text-muted">To see your own profiles, sign in below.</p>
        </section>
      ) : null}
      <PersonSignInForm className="mt-6" onSignedIn={() => navigate("/me")} onStaffSignedIn={staff ? undefined : () => navigate("/admin")} />
      <div className="mt-8 flex flex-col gap-3 border-t border-line pt-6 text-[15px] text-muted">
        {emailEnabled ? (
          <p>
            <strong className="text-ink">Set a password and forgot it?</strong> <Link to="/forgot-password">Reset your password</Link>, or just
            use a sign-in link.
          </p>
        ) : (
          <p>
            <strong className="text-ink">No password yet?</strong> Open your results on the phone or computer you took it on.
          </p>
        )}
        <p>
          <strong className="text-ink">Gifts Profile staff?</strong> Tap “Sign in with a password instead”: your staff email and password
          open the admin. Staff passkeys and sign-in links are on the <Link to="/admin">admin sign-in</Link>.
        </p>
        <p>
          <Link to="/">Take the profile</Link>
        </p>
      </div>
    </Page>
  );
}

/**
 * Where an emailed sign-in link lands (/sign-in/link#token). Opening it uses nothing (email
 * scanners fetch links); the button does. The token is in the fragment, read once and taken
 * out of the address bar.
 */
export function SignInLinkPage() {
  const navigate = useNavigate();
  const [token] = useState(() => window.location.hash.replace(/^#/, ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);

  useEffect(() => {
    if (window.location.hash) history.replaceState(null, "", window.location.pathname);
  }, []);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      const step = await api.useSignInLink(token);
      if (step.done) navigate("/me", { replace: true });
      else setChallenge(step.challenge);
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError && err.status === 404 ? err.message : "Something went wrong. Check your connection and try again.");
    }
  };

  return (
    <Page>
      <h1 className="heading mt-10 text-[30px] leading-[1.1]">Sign in to your profiles</h1>
      {challenge ? (
        <CodeStep className="mt-6" challenge={challenge} onSignedIn={() => navigate("/me", { replace: true })} onStartOver={() => navigate("/sign-in")} />
      ) : token ? (
        <>
          <p className="mt-4 text-[17px] leading-snug">This link signs you in once.</p>
          <button type="button" className="btn btn-primary mt-6 w-full" onClick={() => void go()} disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          {error ? (
            <>
              <p role="alert" className="mt-4 text-[15px] text-danger">
                {error}
              </p>
              <p className="mt-3 text-[15px]">
                <Link to="/sign-in">Ask for a new link</Link>
              </p>
            </>
          ) : null}
        </>
      ) : (
        <p className="mt-4 text-[17px] leading-snug">
          This link is incomplete. Open the whole link from the email, or <Link to="/sign-in">ask for a new one</Link>.
        </p>
      )}
    </Page>
  );
}

export function MyProfilesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<MyDashboard | null>(null);
  const [failed, setFailed] = useState(false);
  // Staff signed in on this browser see a way into the admin; everyone else sees nothing extra.
  const { staff, signOut: staffSignOut } = useStaffSignedIn();
  useEffect(() => {
    api
      .myProfiles()
      .then(setData)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/sign-in", { replace: true });
        else setFailed(true);
      });
  }, [navigate]);

  // Sign out means this browser: any staff session goes too, so it can't linger on a shared
  // device. Always, not only once the staff check has answered: a quick click mustn't leave one
  // behind (with no staff session the call is a harmless "success" that sets no cookie).
  const signOut = async () => {
    await Promise.all([api.signOut().catch(() => undefined), staffSignOut()]);
    navigate("/", { replace: true });
  };

  if (failed) {
    return (
      <Page>
        <p className="mt-16 text-muted">We couldn't load your profiles. Reload the page to try again.</p>
      </Page>
    );
  }
  if (!data) return <Loading label="Loading your profiles" />;

  const done = data.profiles.filter((p) => p.status === "completed");
  // Every started profile, answered or not: someone who paid and stopped before question 1 must
  // see it here to carry on (Edward, Oct 23).
  const open = data.profiles.filter((p) => p.status === "in_progress");

  return (
    <Page
      width="wide"
      right={
        <span className="inline-flex items-center gap-4">
          {staff ? <GoToAdminLink /> : null}
          <button type="button" className="underline" onClick={() => void signOut()}>
            Sign out
          </button>
        </span>
      }
    >
      <p className="eyebrow mt-8 text-accent-text">My Gifts Profile</p>
      <h1 className="heading mt-2 text-[30px] leading-[1.1]">{data.person.firstName ? `Welcome back, ${data.person.firstName}` : "Welcome back"}</h1>
      <p className="mt-2 text-[15px] text-muted">{data.person.email}</p>

      {open.length > 0 ? (
        <section className="mt-8" aria-labelledby="open-title">
          <h2 id="open-title" className="heading text-[21px]">
            Not finished
          </h2>
          <ul className="mt-3 flex flex-col gap-3">
            {open.map((p) => (
              <ProfileCard key={p.takeId} p={p} />
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mt-8" aria-labelledby="done-title">
        <h2 id="done-title" className="heading text-[21px]">
          Your results
        </h2>
        {done.length === 0 ? (
          <p className="mt-3 text-[15px] text-muted">No finished profiles yet.</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-3" aria-label="Your results">
            {done.map((p) => (
              <ProfileCard key={p.takeId} p={p} />
            ))}
          </ul>
        )}
        {done.length > 0 ? (
          <p className="mt-4 text-[15px]">
            <Link to="/">Take it again</Link>
          </p>
        ) : null}
      </section>

      {done.length >= 2 ? <ComparePicker profiles={done} /> : null}

      <MyTeams />

      <MyConnectedApps />

      <MySignIn />

      <section className="mt-10" aria-labelledby="described-title">
        <h2 id="described-title" className="heading text-[21px]">
          People you described
        </h2>
        {data.described.length === 0 ? (
          <p className="mt-3 text-[15px] text-muted">When you answer for someone who sent you their link while you're signed in, they'll show here.</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-2 text-[15px]" aria-label="People you described">
            {data.described.map((d, i) => (
              <li key={i} className="flex justify-between gap-3 rounded-[12px] border border-line bg-panel px-4 py-3">
                <span className="font-semibold">{d.firstName}</span>
                <span className="text-muted">{d.completedAt ? formatDate(d.completedAt) : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Page>
  );
}

/** Two finished profiles, side by side on one map: newest and the one before, to start with. */
function ComparePicker({ profiles }: { profiles: MyProfile[] }) {
  const navigate = useNavigate();
  const [a, setA] = useState(profiles[1]!.takeId);
  const [b, setB] = useState(profiles[0]!.takeId);
  const when = (p: MyProfile) => p.completedAt ?? p.startedAt;
  // Two on the same day: the time tells them apart.
  const sameDay = (p: MyProfile) => profiles.filter((q) => formatDate(when(q)) === formatDate(when(p))).length > 1;
  const label = (p: MyProfile) =>
    `${formatDate(when(p))}${sameDay(p) ? `, ${new Date(when(p)).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : ""} · ${p.context.label}`;
  const go = () => {
    if (a === b) return;
    // Older first, whichever way round they picked them.
    const at = (id: string) => Date.parse(profiles.find((p) => p.takeId === id)!.completedAt ?? "");
    const [older, newer] = at(a) <= at(b) ? [a, b] : [b, a];
    navigate(`/compare/${older}/${newer}`);
  };
  return (
    <section className="mt-10" aria-labelledby="compare-title">
      <h2 id="compare-title" className="heading text-[21px]">
        Compare two profiles
      </h2>
      <p className="mt-1 text-[15px] text-muted">See both on the map, and what changed in your domain, gifts and theme.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1.5 text-[14px] font-semibold">
          This one
          <select className="field-input min-w-0" value={a} onChange={(e) => setA(e.target.value)}>
            {profiles.map((p) => (
              <option key={p.takeId} value={p.takeId}>
                {label(p)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1.5 text-[14px] font-semibold">
          With
          <select className="field-input min-w-0" value={b} onChange={(e) => setB(e.target.value)}>
            {profiles.map((p) => (
              <option key={p.takeId} value={p.takeId}>
                {label(p)}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn btn-primary min-h-12 sm:col-span-2" disabled={a === b} onClick={go}>
          Compare
        </button>
      </div>
    </section>
  );
}

/** Teams I'm in (what I share, leave) and the ones I lead (their report). */
function MyTeams() {
  const [teams, setTeams] = useState<MyTeam[] | null>(null);
  const [consentText, setConsentText] = useState("");
  const load = () =>
    api
      .myTeams()
      .then((r) => {
        setTeams(r.teams);
        setConsentText(r.consentText);
      })
      .catch(() => setTeams([]));
  useEffect(() => {
    void load();
  }, []);
  if (!teams || teams.length === 0) return null;
  const leave = async (t: MyTeam) => {
    if (!window.confirm(`Leave ${t.name}? Your results stop counting in its report.`)) return;
    await api.leaveTeam(t.teamId).catch(() => undefined);
    await load();
  };
  // Only this row changes when they agree, so the others (and any open buttons) stay put.
  const agreed = (teamId: string, consentVersion: number) =>
    setTeams((all) => all?.map((t) => (t.teamId === teamId ? { ...t, consentVersion, needsConsent: false } : t)) ?? all);
  return (
    <section className="mt-10" aria-labelledby="teams-title">
      <h2 id="teams-title" className="heading text-[21px]">
        Your teams
      </h2>
      <ul className="mt-3 flex flex-col gap-2" aria-label="Your teams">
        {teams.map((t) => (
          <TeamRow key={t.teamId} t={t} consentText={consentText} onLeave={() => void leave(t)} onAgreed={(v) => agreed(t.teamId, v)} />
        ))}
      </ul>
    </section>
  );
}

function TeamRow({ t, consentText, onLeave, onAgreed }: { t: MyTeam; consentText: string; onLeave: () => void; onAgreed: (consentVersion: number) => void }) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justAgreed, setJustAgreed] = useState(false);
  const shared = useRef<HTMLParagraphElement>(null);

  // The button disappears when they agree, so put focus on the confirmation instead of dropping it.
  useEffect(() => {
    if (justAgreed) shared.current?.focus();
  }, [justAgreed]);

  const agree = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.shareFullResults(t.teamId);
      setJustAgreed(true);
      onAgreed(r.consentVersion);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 404
          ? "We couldn't find your shared profile for this team. Reload the page and try again."
          : "That didn't work. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-[12px] border border-line bg-panel px-4 py-3" data-testid="team-row">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span>
          <span id={`${id}-name`} className="font-semibold">
            {t.name}
          </span>
          <span className="block text-[13px] text-muted">
            {t.leader ? "You lead this team" : "Member"}
            {t.sharedCompletedAt ? ` · sharing your profile from ${formatDate(t.sharedCompletedAt)}` : t.sharedTakeId ? " · your profile isn't finished yet" : ""}
          </span>
        </span>
        <span className="flex gap-2">
          {t.leader ? (
            <Link to={`/teams/${t.teamId}`} className="btn btn-primary min-h-11 px-4 text-[13px] no-underline">
              Team report
            </Link>
          ) : null}
          <button type="button" className="btn btn-secondary min-h-11 px-4 text-[13px]" onClick={onLeave}>
            Leave
          </button>
        </span>
      </div>

      {t.sharedTakeId && t.needsConsent ? (
        <div className="mt-3 border-t border-line pt-3" data-testid="reshare">
          <p id={`${id}-why`} className="text-[14px] leading-snug">
            When you joined, this team's leaders could only see the team report. You can choose to let them see your full results.
          </p>
          <p className="mt-2 rounded-[12px] border border-line bg-soft px-3 py-2 text-[13px] leading-snug text-muted">
            <span className="sr-only">The wording you'd agree to: </span>
            {consentText}
          </p>
          <button
            type="button"
            className="btn btn-primary mt-3 min-h-11 w-full px-4 text-[14px] sm:w-auto"
            disabled={busy}
            aria-describedby={`${id}-name ${id}-why`}
            onClick={() => void agree()}
          >
            {busy ? "Sharing…" : "Share my full results with the leaders"}
          </button>
          {error ? (
            <p role="alert" className="mt-2 text-[14px] text-danger">
              {error}
            </p>
          ) : null}
        </div>
      ) : t.sharedTakeId ? (
        <p
          ref={shared}
          tabIndex={-1}
          role={justAgreed ? "status" : undefined}
          className={`mt-2 border-t border-line pt-2 text-[13px] outline-none ${justAgreed ? "text-ink" : "text-muted"}`}
          data-testid="full-results-shared"
        >
          Your full results are shared with this team's leaders.
        </p>
      ) : null}
    </li>
  );
}

/**
 * AI apps this person has connected to their results. Quiet on purpose: nothing shows until
 * they have connected one, since most people never will.
 */
function MyConnectedApps() {
  const [data, setData] = useState<MyConnections | null>(null);
  const load = () =>
    api
      .myConnections()
      .then(setData)
      .catch(() => setData(null));
  useEffect(() => {
    void load();
  }, []);
  if (!data || data.connections.length === 0) return null;
  const disconnect = async (id: string) => {
    await api.disconnectApp(id);
    await load();
  };
  return (
    <section className="mt-10" aria-labelledby="apps-title">
      <h2 id="apps-title" className="heading text-[21px]">
        Connected apps
      </h2>
      <p className="mt-2 text-[15px] text-muted">AI apps you've allowed to look at your results. You can disconnect one any time.</p>
      {data.aiAccess.enabled ? null : (
        <p className="mt-3 rounded-[12px] border border-line bg-soft px-4 py-3 text-[14px] leading-snug" data-testid="ai-access-off">
          AI access isn't switched on for your account yet, so these apps won't see any results until the Gifts Profile team turns it on.
        </p>
      )}
      <ConnectedAppsList label="Connected apps" connections={data.connections} onDisconnect={(c) => disconnect(c.id)} />
    </section>
  );
}

function ProfileCard({ p }: { p: MyProfile }) {
  const context =
    p.context.kind === "event" && p.context.phase
      ? `${p.context.label} · ${p.context.phase === "pre" ? "before" : "after"}`
      : p.context.label;
  const finished = p.status === "completed";
  return (
    <li className="rounded-[16px] border border-line bg-panel p-4" data-testid="profile-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="heading text-[18px]">{finished ? (p.profileName ?? "Your profile") : p.answeredCount > 0 ? "In progress" : "Not started yet"}</p>
        <p className="text-[14px] text-muted">{formatDate(p.completedAt ?? p.startedAt)}</p>
      </div>
      <p className="mt-1 text-[14px] text-muted">
        {context}
        {finished && p.quadrant ? ` · ${p.quadrant}` : ""}
      </p>
      {finished && p.raters.total > 0 ? (
        <p className="mt-1 text-[14px] text-muted">
          {p.raters.completed} of {p.raters.total} {p.raters.completed === 1 ? "person has" : "people have"} answered
        </p>
      ) : null}
      <Link to={finished ? `/results/${p.takeId}` : `/take/${p.takeId}`} className="btn btn-secondary mt-3 inline-flex min-h-11 px-4 text-[14px] no-underline">
        {finished ? "See results" : p.answeredCount > 0 ? "Continue" : "Start"}
      </Link>
    </li>
  );
}

/**
 * On a results page: save them with a password (first time), or sign in to see the rest.
 * Shown only to whoever opened this result (the API checks), never asks for an email: it's
 * the one this profile was taken with.
 */
/** A passkey for next time (optional), once signed in and nothing stands in the way. */
function PasskeyOffer() {
  const [supported] = useState(passkeysSupported);
  const [offer, setOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; alert: boolean } | null>(null);
  useEffect(() => {
    if (!supported) return;
    let live = true;
    // Not for someone who has one already, or whose authenticator app would ask for a code first.
    api
      .security()
      .then((s) => {
        if (!live || s.passkeys.length > 0 || s.totp.on) return;
        setOffer(true);
        void prepareAdd().catch(() => undefined);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [supported]);
  if (!offer && !message) return null;
  const add = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await addPasskey();
      setOffer(false);
      setMessage({ text: "Passkey added. Next time, tap “Sign in with a passkey”.", alert: false });
    } catch (err) {
      setMessage({ text: err instanceof PasskeyError ? err.message : "The passkey wasn't added. You can add one later from My profiles.", alert: true });
      void prepareAdd().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {offer ? (
        <>
          <p className="text-pretty mt-3 text-[15px] leading-snug text-muted">Optional: sign in with Face ID or Touch ID next time.</p>
          <button type="button" className="btn btn-secondary mt-3 w-full sm:w-auto sm:px-6" disabled={busy} onClick={() => void add()}>
            {busy ? "Waiting for your device…" : "Add a passkey"}
          </button>
        </>
      ) : null}
      {message ? (
        <p role={message.alert ? "alert" : "status"} className={`mt-3 text-[14px] ${message.alert ? "text-danger" : "text-muted"}`}>
          {message.text}
        </p>
      ) : null}
    </>
  );
}

/** Whether this result's viewer is signed in as its owner, and its email (null until it answers). */
export function useTakeAccount(takeId: string, resultKey?: string | null): TakeAccount | null {
  const [state, setState] = useState<TakeAccount | null>(null);
  useEffect(() => {
    let live = true;
    api
      .takeAccount(takeId, resultKey)
      .then((s) => live && setState(s))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [takeId, resultKey]);
  return state;
}

/** "Email me a sign-in link" to this profile's email: the button, then what the API said. */
function useSendLink(email: string | undefined) {
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    if (!email) return;
    setBusy(true);
    setError(null);
    try {
      setSent((await api.requestSignInLink(email)).message);
    } catch {
      setError("Sorry, we couldn't send that. Mind trying again?");
    } finally {
      setBusy(false);
    }
  };
  return { sent, busy, error, send };
}

/**
 * At the top of a result opened from its results email (Edward, Nov 3): that link shows only this
 * profile, since the email may have been forwarded, so this just offers to log in to see more,
 * kept generic ("you're showing way too much information here"): no email address, no rules.
 * Sign-in emails a one-time link (apps/api/src/services/person-sign-in.ts). Not when signed in as
 * them, or with email off.
 */
export function SeeAllProfilesCard({ account }: { account: TakeAccount | null }) {
  const emailEnabled = useEmailEnabled() === true;
  if (!account || account.signedIn || !emailEnabled) return null;
  return (
    <section
      className="mt-6 flex items-center justify-between gap-4 rounded-[16px] border border-line bg-panel px-4 py-3 sm:px-5"
      aria-labelledby="see-all-title"
      data-testid="see-all-profiles"
    >
      <h2 id="see-all-title" className="text-[16px] font-semibold">
        Want to see more?
      </h2>
      <Link to="/sign-in" className="btn btn-secondary shrink-0 px-6 no-underline">
        Log in
      </Link>
    </section>
  );
}

/**
 * On a results page: they're saved already (Edward, Oct 23: "we wouldn't even need to say
 * that... they're already saved"), and how to come back: an emailed sign-in link to the email
 * this profile was taken with, no password. Signed in: a passkey for next time, if they like.
 * Shown only to whoever opened this result (the API checks). Opened from a results email and not
 * signed in, SeeAllProfilesCard at the top says it instead (`fromEmail`).
 */
export function SaveResultsCard({ account, fromEmail = false }: { account: TakeAccount | null; fromEmail?: boolean }) {
  const emailEnabled = useEmailEnabled() === true;
  const { sent, busy, error, send } = useSendLink(account?.email);
  if (!account) return null;
  if (fromEmail && !account.signedIn && emailEnabled) return null;

  return (
    <section className="mt-8 rounded-[16px] border border-line bg-panel p-5" aria-labelledby="saved-title" data-testid="save-results">
      <h2 id="saved-title" className="heading text-[19px] leading-tight">
        Your results are saved
      </h2>
      {account.signedIn ? (
        <>
          <p className="mt-2 text-[15px]" data-testid="my-profiles-link">
            <Link to="/me">See all your profiles</Link>
          </p>
          <PasskeyOffer />
        </>
      ) : (
        <>
          <p className="text-pretty mt-2 text-[15px] leading-snug text-muted">
            To see them again on any phone or computer, sign in at <Link to="/sign-in">giftsprofile.com/sign-in</Link> with{" "}
            <strong className="font-semibold text-ink [overflow-wrap:anywhere]">{account.email}</strong>. We'll email you a link, so there's no password to remember.
          </p>
          {emailEnabled ? (
            sent ? (
              <p role="status" className="mt-3 text-[14px] text-muted">
                {sent}
              </p>
            ) : (
              <button type="button" className="btn btn-secondary mt-4 w-full sm:w-auto sm:px-6" disabled={busy} onClick={() => void send()}>
                {busy ? "Sending…" : "Email me a sign-in link"}
              </button>
            )
          ) : null}
          {error ? (
            <p role="alert" className="mt-3 text-[14px] text-danger">
              {error}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
